import { esc, safeHref } from '../utils/formatters';
import { renderKeyedStack } from '../utils/dom-list';
import { LAYER_POINT_SOURCES, LAYER_POINT_TYPES } from '../core/layer-point-sources';

// Sections of the panel in their order: objects first, then what the switched-on overlay layers say about the place.
export const POINT_SOURCE_LABELS = {
    meshCoverage: 'Widoki 3D',
    transaction: 'Transakcje', parcel: 'Działki', building: 'Budynki',
    gunb: 'Pozwolenia na budowę', listing: 'Ogłoszenia', signal: 'Sygnały społeczności',
    otoczenie: 'Otoczenie', education: 'Szkoły, przedszkola i żłobki', noise: 'Hałas',
    kowr: 'Działki w Zasobie Własności Rolnej Skarbu Państwa',
    ...Object.fromEntries(LAYER_POINT_SOURCES.map(definition => [definition.type, definition.label])),
};

// Hits that describe the place rather than an object on it: they neither count as found objects nor hide the empty state of object layers.
const PLACE_TYPES = new Set(['meshCoverage', ...LAYER_POINT_TYPES]);
const LAYER_TYPES = new Set(LAYER_POINT_TYPES);

/** Objects found at the point; answers of overlay layers and the 3D coverage describe the place itself. */
export function objectHits(session) {
    return session.hits.filter(hit => !PLACE_TYPES.has(hit.type));
}

/**
 * Objects found at the point, for the „Lista” tab counter; answers about the place itself are not
 * objects, so a point with only those has no counter (null).
 */
export function pointQueryObjectCount(session) {
    const count = objectHits(session).length;
    return count || session.hits.length === 0 ? count : null;
}

function renderSourceLink(link) {
    const href = safeHref(link && link.url);
    return href && /^https?:/i.test(href) ? '<a class="ms-button ms-button--secondary ms-button--wrap" href="' + esc(href) + '" target="_blank" rel="noopener noreferrer">'
        + esc(link.label) + '<span class="sr-only"> (otwiera się w nowej karcie)</span></a>' : '';
}

// The alert of a failed source: in its section's slot, so a retry answers where the alert stood. The retry names
// its slot, so focus can move to the section's heading while the alert is replaced (index.js).
function sourceStatusEntry(session, source, label, slot) {
    const hasHits = session.hits.some(hit => source.types.includes(hit.type));
    const message = hasHits
        ? 'Pokazujemy dostępne wyniki. Dodatkowe sprawdzenie źródła „' + esc(source.label || label) + '” nie powiodło się; lista może być niepełna.'
        : 'Nie udało się sprawdzić źródła: ' + esc(source.label || label) + '.';
    const retry = '<button class="ms-button ms-button--secondary" type="button" data-point-retry="' + esc(source.key) + '" data-point-slot="' + esc(slot) + '">Spróbuj ponownie</button>';
    // A failed overlay layer also links to its source service, which answers for the place directly.
    return {
        key: 'status:' + source.key,
        html: '<div class="ms-alert ms-alert--warning nm-point-status" role="status"><p>' + message + '</p>'
            + '<div class="ms-alert__actions">' + retry + renderSourceLink(source.errorLink) + '</div></div>',
    };
}

function sourceLabel(source) {
    return [...new Set(source.types.map(type => POINT_SOURCE_LABELS[type]).filter(Boolean))].join(', ') || source.label;
}

export function pointQueryPanelEntries(session, renderHit, options = {}) {
    const entries = [{ key: 'back', html: '<button class="ms-button ms-button--secondary" type="button" data-point-back>← Wróć do listy</button>' }];
    const order = Object.keys(POINT_SOURCE_LABELS);
    // A source answers in the slot of its last section, so every section keeps its place in every state.
    const slotOf = source => source.types.reduce((last, type) => order.indexOf(type) > order.indexOf(last) ? type : last, source.types[0]);
    Object.entries(POINT_SOURCE_LABELS).forEach(([type, label]) => {
        const hits = session.hits.filter(hit => hit.type === type);
        const sources = session.sources.filter(source => source.types.includes(type));
        if (!hits.length && !sources.length) return;
        const layer = LAYER_TYPES.has(type);
        const slotSources = sources.filter(source => slotOf(source) === type);
        const failed = slotSources.filter(source => source.state === 'error');
        // An overlay layer has its section from the click on: loading, a failure and the answer replace each other in place.
        const pending = layer && !hits.length ? slotSources.filter(source => source.state === 'loading') : [];
        if (hits.length || (layer && (failed.length || pending.length))) entries.push({ key: 'heading:' + type, html: '<h3>' + esc(label) + '</h3>' });
        hits.forEach(hit => entries.push({
            key: type + ':' + hit.id,
            html: '<div data-point-hit-type="' + esc(type) + '" data-point-hit-id="' + esc(String(hit.id)) + '">' + renderHit(hit) + '</div>',
        }));
        pending.forEach(source => entries.push({
            key: 'status:' + source.key,
            html: '<div class="ms-empty-state nm-loading-state nm-loading-state--inline" role="status" aria-busy="true"><span class="ms-spinner" aria-hidden="true"></span><span>Sprawdzamy to miejsce…</span></div>',
        }));
        failed.forEach(source => entries.push(sourceStatusEntry(session, source, label, type)));
    });
    const emptyLabels = new Set();
    const loadingLabels = new Set();
    session.sources.forEach(source => {
        const label = sourceLabel(source);
        const hasHits = session.hits.some(hit => source.types.includes(hit.type));
        if (!hasHits && (source.state === 'empty' || source.state === 'success')) emptyLabels.add(label);
        // Overlay layers show their own progress in their sections.
        if (source.state === 'loading' && !source.types.every(type => LAYER_TYPES.has(type))) loadingLabels.add(label);
    });
    if (loadingLabels.size) entries.push({ key: 'loading',
        html: '<div class="ms-empty-state nm-loading-state" role="status" aria-busy="true"><span class="ms-spinner" aria-hidden="true"></span>'
            + '<span>Sprawdzamy: ' + esc([...loadingLabels].join(', ')) + '…</span></div>',
    });
    if (emptyLabels.size && session.hits.length) entries.push({ key: 'empty-sources',
        html: '<p class="nm-point-empty-sources" data-point-empty-sources>Bez wyników w tym miejscu: ' + esc([...emptyLabels].join(', ')) + '.</p>',
    });
    if (!session.loading && !session.hits.length) entries.push({ key: 'empty', html: '<div class="ms-empty-state"><p>'
        + (session.sources.some(source => source.state === 'error')
            ? session.sources.some(source => source.state === 'empty' || source.state === 'success')
                ? 'W sprawdzonych źródłach nie znaleziono obiektów. Część źródeł jest niedostępna; możesz ponowić sprawdzenie.'
                : 'Nie udało się sprawdzić źródeł dla tego miejsca. Możesz ponowić sprawdzenie.'
            : 'Nie znaleziono obiektów w tym miejscu dla wybranych warstw i filtrów.')
        + '</p></div>' });
    if (!session.loading && !objectHits(session).length && options.zoomGates && options.zoomGates.length) entries.push({
        key: 'zoom-gates', html: renderZoomGates(options.zoomGates, options.zoom),
    });
    return entries;
}

function formatZoomLevel(zoom) {
    return Number.isInteger(zoom) ? String(zoom) : zoom.toFixed(1).replace('.', ',');
}

// Layers that are switched on but are only queried once the map is zoomed in
// far enough; the action zooms to the level that unlocks all of them.
function renderZoomGates(gates, zoom) {
    const targetZoom = Math.max(...gates.map(gate => gate.minZoom));
    return '<section class="ms-card nm-zoom-gates" data-point-zoom-gates aria-labelledby="nm-zoom-gates-title">'
        + '<header class="ms-card__header"><h3 class="ms-card__title" id="nm-zoom-gates-title">Warstwy dostępne po przybliżeniu</h3>'
        + (zoom != null ? '<span class="nm-zoom-gates__current">Obecny poziom: <strong>' + esc(formatZoomLevel(zoom)) + '</strong></span>' : '')
        + '</header>'
        + '<div class="ms-card__body"><p class="nm-zoom-gates__intro">Te warstwy są włączone, ale przy obecnym przybliżeniu nie sprawdzamy ich w klikniętym miejscu. '
        + 'Mogą tu mieć dane, które zobaczysz dopiero po przybliżeniu mapy.</p>'
        + '<ul class="nm-zoom-gates__list">'
        + gates.map(gate => '<li class="nm-zoom-gates__item"><span class="nm-zoom-gates__name">'
            + '<span class="nm-zoom-gates__swatch nm-zoom-gates__swatch--' + esc(gate.type) + '" aria-hidden="true"></span>' + esc(gate.label) + '</span>'
            + '<span class="ms-badge ms-badge--neutral">od poziomu ' + esc(formatZoomLevel(gate.minZoom)) + '</span></li>').join('')
        + '</ul></div>'
        + '<footer class="ms-card__footer"><button class="ms-button ms-button--primary ms-button--block" type="button" data-point-zoom-to="' + esc(String(targetZoom)) + '">'
        + 'Przybliż do poziomu ' + esc(formatZoomLevel(targetZoom)) + ' i sprawdź ponownie</button></footer>'
        + '</section>';
}

export function pointQueryHeading(session) {
    const objects = objectHits(session);
    const hasCoverage = session.hits.some(hit => hit.type === 'meshCoverage');
    const hasLayerResults = session.hits.some(hit => LAYER_TYPES.has(hit.type));
    const hasErrors = session.sources.some(source => source.state === 'error');
    const hasCheckedSources = session.sources.some(source => source.state === 'empty' || source.state === 'success');
    return {
        title: objects.length === 1 && objects[0].type === 'parcel' ? 'Znaleziono 1 działkę'
            : objects.length ? 'Znalezione obiekty: ' + objects.length
                : hasLayerResults ? 'Informacje z warstw mapy'
                    : hasCoverage ? 'Wybrane miejsce' : session.loading ? 'Sprawdzamy wybrane miejsce'
                        : hasErrors ? hasCheckedSources ? 'Brak wyników w sprawdzonych źródłach' : 'Nie udało się sprawdzić miejsca'
                            : 'Brak wyników w wybranym miejscu',
        meta: session.loading ? 'Trwa sprawdzanie źródeł. Dostępne wyniki możesz już otworzyć.'
            : objects.length ? 'Wybierz obiekt, aby zobaczyć szczegóły i jego otoczenie.'
                : hasLayerResults ? 'Dane włączonych warstw dla wybranego punktu na mapie.'
                    : hasCoverage ? 'Możesz uruchomić symulację dla wybranego miejsca.'
                        : hasErrors ? 'Ponów sprawdzenie niedostępnych źródeł.' : 'Możesz wybrać inne miejsce lub zmienić warstwy i filtry.',
    };
}

export function renderPointQueryPanel(session, config) {
    const heading = pointQueryHeading(session);
    config.title.textContent = heading.title;
    config.meta.textContent = heading.meta;
    const entries = pointQueryPanelEntries(session, config.renderHit, config);
    // Keep both the scrolling sheet and nested content anchored during incremental updates.
    const scroll = [];
    for (let el = config.content; el; el = el.parentElement) scroll.push([el, el.scrollTop]);
    renderKeyedStack(config.content, entries);
    // When a tall alert near the end gives way to a shorter answer, the browser would clamp a scrolled container
    // and pull the section out of view: the stack keeps just the height that holds every scroll position, and no
    // more, so a reader near the top sees no empty space.
    const stack = config.content.querySelector(':scope > [data-keyed-stack]');
    if (stack) {
        stack.style.minHeight = '';
        const deficit = Math.max(0, ...scroll.map(([el, top]) => top - (el.scrollHeight - el.clientHeight)));
        if (deficit > 0) {
            stack.style.minHeight = (stack.offsetHeight + deficit) + 'px';
            stack.__pointHeld = true;
        } else {
            stack.__pointHeld = false;
        }
    }
    scroll.forEach(([el, top]) => { el.scrollTop = top; });
}

/** The list leaves the point query: its stack takes its own height again. */
export function releasePointQueryPanel(content) {
    const stack = content && content.querySelector(':scope > [data-keyed-stack]');
    if (!stack || !stack.__pointHeld) return;
    stack.__pointHeld = false;
    stack.style.minHeight = '';
}
